
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { History, Calendar, Filter, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

// Sample data for transactions
const transactionData = [
  { refNum: "SH-001", date: "2025-05-10", time: "09:25 AM", amount: 127.50, type: "JimatShop" },
  { refNum: "WK-002", date: "2025-05-10", time: "10:30 AM", amount: 95.75, type: "Walk-In" },
  { refNum: "SH-003", date: "2025-05-09", time: "11:15 AM", amount: 210.30, type: "JimatShop" },
  { refNum: "WK-004", date: "2025-05-09", time: "01:45 PM", amount: 74.25, type: "Walk-In" },
  { refNum: "SH-005", date: "2025-05-08", time: "02:20 PM", amount: 156.99, type: "JimatShop" },
  { refNum: "WK-006", date: "2025-05-08", time: "03:30 PM", amount: 118.50, type: "Walk-In" },
  { refNum: "SH-007", date: "2025-05-07", time: "04:10 PM", amount: 82.25, type: "JimatShop" },
  { refNum: "WK-008", date: "2025-05-07", time: "05:05 PM", amount: 195.75, type: "Walk-In" },
];

// Stats data for different durations
const statsData = {
  daily: {
    transactions: 78,
    averageOrderValue: 112.35,
    jimatshopSales: 4150.50,
    walkInSales: 3685.25
  },
  weekly: {
    transactions: 495,
    averageOrderValue: 116.85,
    jimatshopSales: 29450.75,
    walkInSales: 28520.80
  },
  monthly: {
    transactions: 2040,
    averageOrderValue: 120.45,
    jimatshopSales: 125600.50,
    walkInSales: 119250.75
  },
  quarterly: {
    transactions: 6120,
    averageOrderValue: 124.75,
    jimatshopSales: 390500.25,
    walkInSales: 372800.50
  },
  yearly: {
    transactions: 24580,
    averageOrderValue: 126.85,
    jimatshopSales: 1550000.75,
    walkInSales: 1560000.25
  }
};

const SalesHistory = () => {
  const [duration, setDuration] = useState<"daily" | "weekly" | "monthly" | "quarterly" | "yearly">("daily");
  const [searchTerm, setSearchTerm] = useState("");

  const handleDurationChange = (value: string) => {
    setDuration(value as "daily" | "weekly" | "monthly" | "quarterly" | "yearly");
  };

  const filteredTransactions = transactionData.filter(transaction => 
    transaction.refNum.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transaction.date.includes(searchTerm) ||
    transaction.time.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentStats = statsData[duration];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Sales History</h1>
          <p className="text-muted-foreground">
            View and analyze historical sales data and trends.
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Filter className="mr-2 h-4 w-4" />
            Filter
          </Button>
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Calendar className="mr-2 h-4 w-4" />
            Date Range
          </Button>
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-[#8CC84B]/20">
        <CardHeader className="bg-[#F2FCE2] border-b border-[#8CC84B]/20">
          <CardTitle className="flex items-center">
            <History className="h-5 w-5 mr-2 text-[#4D9E41]" />
            Sales History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="bg-[#F2FCE2] border border-[#8CC84B]/20">
              <TabsTrigger value="transactions" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
                Transactions
              </TabsTrigger>
              <TabsTrigger value="products" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
                Products
              </TabsTrigger>
              <TabsTrigger value="customers" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
                Customers
              </TabsTrigger>
              <TabsTrigger value="promotions" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
                Promotions
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="transactions" className="mt-6 space-y-4">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Filter by Duration</h3>
                  <Select value={duration} onValueChange={handleDurationChange}>
                    <SelectTrigger className="w-[180px] border-[#8CC84B]/20">
                      <SelectValue placeholder="Select Duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card className="border-[#8CC84B]/20 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{currentStats.transactions}</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-[#8CC84B]/20 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">RM {currentStats.averageOrderValue.toFixed(2)}</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-[#8CC84B]/20 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">JimatShop Sales</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">RM {currentStats.jimatshopSales.toFixed(2)}</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-[#8CC84B]/20 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Walk-In Sales</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">RM {currentStats.walkInSales.toFixed(2)}</div>
                    </CardContent>
                  </Card>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Transactions List</h3>
                    <div className="w-64">
                      <Input 
                        placeholder="Search by ref, date or time"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-[#8CC84B]/20"
                      />
                    </div>
                  </div>
                  
                  <div className="border rounded-md border-[#8CC84B]/20">
                    <Table>
                      <TableHeader className="bg-[#F2FCE2]">
                        <TableRow>
                          <TableHead className="text-[#4D9E41]">Ref Number</TableHead>
                          <TableHead className="text-[#4D9E41]">Date</TableHead>
                          <TableHead className="text-[#4D9E41]">Time</TableHead>
                          <TableHead className="text-[#4D9E41]">Type</TableHead>
                          <TableHead className="text-right text-[#4D9E41]">Amount (RM)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTransactions.map((transaction) => (
                          <TableRow key={transaction.refNum} className="hover:bg-[#F2FCE2]/50">
                            <TableCell className="font-medium">{transaction.refNum}</TableCell>
                            <TableCell>{transaction.date}</TableCell>
                            <TableCell>{transaction.time}</TableCell>
                            <TableCell>
                              <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                                transaction.type === "JimatShop" 
                                  ? "bg-[#F2FCE2] text-[#4D9E41]"
                                  : "bg-[#E5F5DA] text-[#8CC84B]"
                              }`}>
                                {transaction.type}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">{transaction.amount.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="products" className="mt-6">
              <p className="text-muted-foreground">Product sales history will be implemented here.</p>
            </TabsContent>
            
            <TabsContent value="customers" className="mt-6">
              <p className="text-muted-foreground">Customer purchase history will be implemented here.</p>
            </TabsContent>
            
            <TabsContent value="promotions" className="mt-6">
              <p className="text-muted-foreground">Promotion history and performance will be implemented here.</p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesHistory;
